import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useNotification } from "../contexts/NotificationContext";
import styled from "styled-components";
import logoBlack from "../Assets/final-logo-black.png";
import logoWhite from "../Assets/final-logo-white.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faBell, faBellSlash } from "@fortawesome/free-solid-svg-icons";

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--background-secondary);
  color: var(--text-primary);
  padding: 0.75rem 1.5rem;
  box-shadow: var(--box-shadow);
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 34px;
  margin-right: 10px;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const LogoSubtitle = styled.div`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-top: 2px;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
`;

const UserEmail = styled.span`
  margin-right: 1rem;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const Button = styled.button`
  background: var(--accent-color-light);
  color: var(--text-primary);
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: background 0.2s;
  margin-left: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ThemeToggle = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  background: rgba(211, 47, 47, 0.8);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  margin-right: 1rem;
  font-size: 0.9rem;
`;

const NavigationBar: React.FC = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { currentUser, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { isMuted, toggleMute } = useNotification();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      setError("");
      setLoading(true);
      await logout();
      navigate("/login");
    } catch (err: any) {
      setError("Failed to log out");
    } finally {
      setLoading(false);
    }
  };

  return (
    <NavContainer>
      <LogoContainer>
        <LogoImage
          src={darkMode ? logoWhite : logoBlack}
          alt="Huntington Hospital Logo"
        />
        <LogoText>
          <LogoTitle>Huntington Hospital</LogoTitle>
          <LogoSubtitle>Arrival Board</LogoSubtitle>
        </LogoText>
      </LogoContainer>

      <UserSection>
        {error && <ErrorMessage>{error}</ErrorMessage>}

        <ThemeToggle onClick={toggleMute} title={isMuted ? "Unmute notifications" : "Mute notifications"}>
          <FontAwesomeIcon icon={isMuted ? faBellSlash : faBell} />
        </ThemeToggle>

        <ThemeToggle onClick={toggleDarkMode} title={darkMode ? "Switch to light mode" : "Switch to dark mode"}>
          <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
        </ThemeToggle>

        {currentUser && (
          <>
            <UserEmail>{currentUser.email}</UserEmail>
            <Button onClick={handleLogout} disabled={loading}>
              {loading ? "Logging out..." : "Sign Out"}
            </Button>
          </>
        )}
      </UserSection>
    </NavContainer>
  );
};

export default NavigationBar;
